import { useEffect, useState } from "react";

import type { Light, VesselSpec, Viewpoint } from "@nav-games/nav-lights";

import { VesselSilhouette } from "./VesselSilhouette";

const COLORS: Record<string, { fill: string; glow: string }> = {
  white: { fill: "#fff7d6", glow: "rgba(255, 247, 214, 0.7)" },
  red: { fill: "#ff3b3b", glow: "rgba(255, 59, 59, 0.7)" },
  green: { fill: "#3bff7a", glow: "rgba(59, 255, 122, 0.7)" },
  yellow: { fill: "#ffd84d", glow: "rgba(255, 216, 77, 0.7)" },
};

interface NavLightsFigureProps {
  vessel: VesselSpec;
  /// Which aspect to render. Defaults to `headOn`.
  viewpoint?: Viewpoint;
  size?: number;
  /// Hide the vessel silhouette and just draw the lights — closer
  /// to what a real mariner sees at night. Default false; the game
  /// turns it on for the "real-world view" mode.
  silhouetteHidden?: boolean;
}

/// Render a vessel's nav lights for a given viewpoint against a
/// night-sky background. Lights animate (flash) when their
/// `characteristic` is "flashing".
export function NavLightsFigure({
  vessel,
  viewpoint = "headOn",
  size = 240,
  silhouetteHidden = false,
}: NavLightsFigureProps) {
  const lights = vessel.views[viewpoint] ?? vessel.views.headOn;

  // Flashing lights toggle at ~120/min (IRPCS Rule 21(f)).
  const [flashOn, setFlashOn] = useState(true);
  useEffect(() => {
    const hasFlashing = lights.some((l) => l.characteristic === "flashing");
    if (!hasFlashing) return;
    const t = setInterval(() => setFlashOn((on) => !on), 500);
    return () => clearInterval(t);
  }, [lights]);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-label={`Nav lights: ${vessel.name} (${viewpoint})`}
      role="img"
      style={{ background: "#020611", borderRadius: 8 }}
    >
      {/* Subtle sea + horizon. Drawn first so lights overlay it. */}
      <rect x={0} y={80} width={100} height={20} fill="#06121e" />
      <line x1={0} y1={80} x2={100} y2={80} stroke="#11283d" strokeWidth={0.5} />

      {!silhouetteHidden && (
        <VesselSilhouette vesselId={vessel.id} viewpoint={viewpoint} />
      )}

      {lights.map((light, i) => (
        <LightDot key={i} light={light} flashOn={flashOn} />
      ))}
    </svg>
  );
}

function LightDot({ light, flashOn }: { light: Light; flashOn: boolean }) {
  const colour = COLORS[light.color];
  const baseR = light.radius ?? 3;
  // Far-side all-round lights (e.g. dredger's opposite-side markers
  // on a beam view) read as "behind the mast" — render smaller and
  // dimmer so the near-side lights still dominate the silhouette.
  const r = light.farSide ? baseR * 0.7 : baseR;
  const dim = light.farSide ? 0.4 : 1;
  const visible = light.characteristic === "flashing" ? flashOn : true;
  if (!colour) return null;
  return (
    <g style={{ transition: "opacity 60ms linear" }}>
      {/* Halo */}
      <circle
        cx={light.x}
        cy={light.y}
        r={r * 2.5}
        fill={colour.glow}
        opacity={visible ? 0.45 * dim : 0}
        style={{ transition: "opacity 60ms linear" }}
      />
      {/* Core */}
      <circle
        cx={light.x}
        cy={light.y}
        r={r}
        fill={colour.fill}
        opacity={visible ? dim : 0}
        style={{ transition: "opacity 60ms linear" }}
      />
    </g>
  );
}
